<template>
  <div>
    <label class="label-infra-size"> Infra Size</label>
    <div class="grid grid-cols-2 gap-x-2">
      <div class="flex">
        <Select
          v-model="values[serviceType].resourceSize.cpuUnit"
          :items="cpuUnitOptions"
          label="CPU Unit"
          class="select-v2 flex-1"
          :solo="false"
          @change="(v) => hanldeChange('cpu', v)"
        />
        <Input
          v-model="values[serviceType].resourceSize.cpu"
          label="CPU - Value"
          type="number"
          appendClass="input-text-field-v2 flex-3"
          :rules="[rules.required, validateTotalSize('cpu')]"
          @blur="validate"
          @change="(v) => hanldeValueChange()"
        />
      </div>

      <div class="flex">
        <Select
          v-model="values[serviceType].resourceSize.memoryUnit"
          :items="memoryUnitOptions"
          label="Memory Unit"
          appendClass="select-v2 flex-1"
          :solo="false"
          @change="(v) => hanldeChange('memory', v)"
        />
        <Input
          v-model="values[serviceType].resourceSize.memory"
          label="Memory - Value"
          type="number"
          appendClass="input-text-field-v2 flex-3"
          :rules="[rules.required, validateTotalSize('memory')]"
          @blur="validate"
          @change="(v) => hanldeValueChange()"
        />
      </div>

      <div class="switch-v2-group col-span-2">
        <Sw
          v-model="values[serviceType].gpuEnabled"
          inset
          appendClass="switch-v2"
          :disabled="!gpuAvailable"
          @input="switchGPUCore"
        ></Sw>
        <span class="text-switch">GPU Configuration</span>
      </div>

      <Input
        v-if="values[serviceType].gpuEnabled"
        v-model="values[serviceType].resourceSize.gpu"
        label="Number of GPUs"
        placeholder="Enter Number of GPUs"
        type="number"
        appendClass="input-text-field-v2"
        :rules="[rules.required, validateGPUInput]"
        :min="1"
        clearable
        @input="inputGPU"
      />
    </div>
  </div>
</template>

<script>
import Input from "@/components/atoms/Input/Input.vue";
import Select from "@/components/atoms/Select/Select.vue";
import { RulesMixin } from "@/mixins/RulesMixin";
import { debounce } from "lodash";

export default {
  props: {
    serviceType: { type: String },
    values: { type: Object },
    requirementInfraResource: { type: Array },
    validate: { type: Function },
    gpuAvailable: { type: Boolean },
    setValue: { type: Function, default: () => {} },
  },
  components: { Select, Input },
  mixins: [RulesMixin],
  data() {
    return {
      cpuUnitOptions: [
        { text: "Core", value: "core" },
        { text: "Millicore", value: "millicore" },
      ],
      memoryUnitOptions: [
        { text: "MiB", value: "MiB" },
        { text: "GiB", value: "GiB" },
      ],
    };
  },
  methods: {
    parseValue(value, name, serviceType) {
      if (
        name === "cpu" &&
        this.values[serviceType || this.serviceType].resourceSize.cpuUnit ===
          "core"
      ) {
        // parse to millicore to compare
        return Number(value) * 1000;
      }

      if (
        name === "memory" &&
        this.values[serviceType || this.serviceType].resourceSize.memoryUnit ===
          "GiB"
      ) {
        // parse to Mib to compare
        return Number(value) * 1024;
      }

      return Number(value);
    },
    hanldeChange: debounce(function (name, value) {
      if (name === "cpu") {
        if (value === "core") {
          this.values[this.serviceType].resourceSize.cpu =
            Number(this.values[this.serviceType].resourceSize.cpu) / 1000;
        } else {
          this.values[this.serviceType].resourceSize.cpu =
            Number(this.values[this.serviceType].resourceSize.cpu) * 1000;
        }
      }

      if (name === "memory") {
        if (value === "GiB") {
          this.values[this.serviceType].resourceSize.memory =
            Number(this.values[this.serviceType].resourceSize.memory) / 1024;
        } else {
          this.values[this.serviceType].resourceSize.memory =
            Number(this.values[this.serviceType].resourceSize.memory) * 1024;
        }
      }
    }, 200),
    validateTotalSize(name) {
      return (valueParam) => {
        let value = this.parseValue(valueParam, name);

        if (this.requirementInfraResource) {
          const selectedMinimum = this.requirementInfraResource?.find(
            (r) => r?.serviceType === this.serviceType,
          )?.[name]?.value;
          if (selectedMinimum && value < selectedMinimum) {
            return `Minimum ${name} of ${
              this.serviceType
            } is ${selectedMinimum} ${name === "cpu" ? "millicore" : "MiB"}`;
          }
        }

        const totalSize = Number(this.values.resourceSize[name]);

        const currentTotal = this.values.serviceTypes.reduce(
          (total, serviceType) => {
            return serviceType !== this.serviceType
              ? total +
                  this.parseValue(
                    Number(this.values[serviceType].resourceSize[name]),
                    name,
                    serviceType,
                  )
              : total + Number(value);
          },
          0,
        );

        if (currentTotal > totalSize)
          return `Total ${name} of the service type exceeds ${totalSize} ${
            name === "cpu" ? "millicore" : "MiB"
          } `;

        return true;
      };
    },
    hanldeValueChange() {
      this.$emit("value-change", {
        cpu: Number(this.values[this.serviceType].resourceSize.cpu),
        memory: Number(this.values[this.serviceType].resourceSize.memory),
      });
    },
    switchGPUCore(value) {
      this.setValue(`${this.serviceType}.resourceSize.gpu`, value ? "1" : "");
    },
    inputGPU(value) {
      if (!Number(value)) {
        this.setValue(`${this.serviceType}.resourceSize.gpu`, "");
        return;
      }

      // Remove all non-digit characters
      const digits = (value + "" || "").replace(/\D/g, "");
      // Remove leading zeros (optional)
      const normalized = digits.replace(/^0+/, "");
      this.setValue(`${this.serviceType}.resourceSize.gpu`, normalized);
    },
    validateGPUInput(value) {
      if (value && Number(value) < 1) return "Minimum GPU is 1";
      return true;
    },
  },
};
</script>

<style lang="scss" scoped>
.label-infra-size {
  font-weight: 500;
  font-size: 12px;
  margin-bottom: 10px;
  margin-left: 5px;
}

.flex-3 {
  flex: 3;
}
</style>
